"use client";

import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import {
  PromptInput,
  PromptInputFooter,
  PromptInputSelect,
  PromptInputSelectContent,
  PromptInputSelectItem,
  PromptInputSelectTrigger,
  PromptInputSelectValue,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from "@/components/ai-elements/prompt-input";
import { AuthModal } from "@/components/auth/auth-modal";
import { Button } from "@/components/ui/button";

// Define available models (excluding dev-only models)
const models = [
  { id: "anthropic/claude-haiku-4.5", name: "Claude Haiku 4.5" },
  { id: "anthropic/claude-sonnet-4.5", name: "Claude Sonnet 4.5" },
  { id: "openai/gpt-5", name: "GPT-5" },
  { id: "openai/gpt-5-mini", name: "GPT-5 Mini" },
  { id: "openai/gpt-5-nano", name: "GPT-5 Nano" },
  { id: "o3", name: "OpenAI o3 (reasoning)" },
  { id: "o3-mini", name: "OpenAI o3-mini (reasoning)" },
  { id: "xai/grok-code-fast-1", name: "Grok Code Fast 1" },
];

const placeholderTexts = [
  "Hey Nudj, create me a community for my YouTube channel",
  "Hey Nudj, create me a community for my Spotify playlist",
  "Hey Nudj, gamify this video for me",
  "Hey Nudj, create educational quiz for my podcast",
];

export function HomeChat() {
  const [input, setInput] = useState("");
  const [model, setModel] = useState(models[0].id);
  const router = useRouter();
  const { data: session, status } = useSession();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderText, setPlaceholderText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [charIndex, setCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  // Timer refs to track and cleanup timeouts
  const mainTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Set mounted state after hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Cursor blink effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  // Typewriter effect for placeholder
  useEffect(() => {
    // Clear any existing timers on cleanup or deps change
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
      mainTimerRef.current = null;
    }
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }

    if (!input && session?.user) {
      const currentFullText = placeholderTexts[placeholderIndex];

      mainTimerRef.current = setTimeout(
        () => {
          if (isTyping) {
            // Typing forward
            if (charIndex < currentFullText.length) {
              setPlaceholderText(currentFullText.slice(0, charIndex + 1));
              setCharIndex(charIndex + 1);
            } else {
              // Finished typing, wait then start deleting
              pauseTimerRef.current = setTimeout(() => {
                setIsTyping(false);
                setCharIndex(currentFullText.length);
                pauseTimerRef.current = null;
              }, 2000);
            }
          } else {
            // Deleting backward
            if (charIndex > 0) {
              setPlaceholderText(currentFullText.slice(0, charIndex - 1));
              setCharIndex(charIndex - 1);
            } else {
              // Finished deleting, move to next text
              setPlaceholderIndex(
                (prevIndex) => (prevIndex + 1) % placeholderTexts.length
              );
              setIsTyping(true);
              setCharIndex(0);
            }
          }
          mainTimerRef.current = null;
        },
        isTyping ? 50 : 30
      ); // Type slower, delete faster
    } else if (!session?.user) {
      // Clear placeholder for non-authenticated users
      setPlaceholderText("");
    }

    // Cleanup function
    return () => {
      if (mainTimerRef.current) {
        clearTimeout(mainTimerRef.current);
        mainTimerRef.current = null;
      }
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
    };
  }, [input, charIndex, isTyping, placeholderIndex, session?.user]);

  // Monitor for when user becomes authenticated
  useEffect(() => {
    // If user just logged in and we have a pending message
    if (session?.user && typeof window !== "undefined") {
      const pendingDataStr = localStorage.getItem("pendingChatMessage");
      if (pendingDataStr && showAuthModal) {
        // User just authenticated, redirect to chat
        setShowAuthModal(false);
        router.push("/chat");
      }
    }
  }, [session, showAuthModal, router]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!input.trim()) return;

    setIsSubmitting(true);

    // Check if user is authenticated
    if (!session?.user) {
      // Store the pending message and show auth modal
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "pendingChatMessage",
          JSON.stringify({
            message: input,
            model: model,
          })
        );
      }
      setShowAuthModal(true);
      setIsSubmitting(false);
      return;
    }

    // If authenticated, store the message and model, then redirect to chat
    // The chat page will handle creating the chat with the initial message
    if (typeof window !== "undefined") {
      localStorage.setItem(
        "pendingChatMessage",
        JSON.stringify({
          message: input,
          model: model,
        })
      );
    }

    // Redirect to chat which will pick up the pending message
    router.push("/chat");
  };

  return (
    <>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />

      {/* Full viewport background */}
      <div className="fixed inset-0 bg-black -z-20" />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-transparent to-blue-500/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-radial from-purple-500/10 via-blue-500/5 to-transparent blur-3xl" />
      </div>

      <div className="relative mx-auto flex h-[calc(100vh-6rem)] max-w-4xl flex-col p-4">
        <div className="flex-1 flex flex-col justify-center items-center relative">
          <div className="w-full max-w-3xl mx-auto flex flex-col gap-6 md:gap-8 px-4">
            {/* Slogan, headline and subtext */}
            <div className="text-center">
              <div className="mb-2">
                <span className="uppercase tracking-wide font-semibold text-xs md:text-sm bg-gradient-to-r from-brand-400 via-indigo-400 to-fuchsia-500 bg-clip-text text-transparent">
                  Engage, Earn, Repeat
                </span>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 md:gap-3 mb-1">
                <span className="text-3xl md:text-5xl font-bold font-ubuntu whitespace-normal text-white">
                  What should we gamify?
                </span>
              </div>
              <h3 className="text-base md:text-lg text-white/70 mt-1">
                Monetize attention. Reward action. Grow engagement.
              </h3>
            </div>

            {/* Glass input form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                duration: 0.6,
              }}
              className="relative"
            >
              {/* Glass effect container */}
              <div className="absolute inset-0 bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 shadow-2xl" />

              <PromptInput
                onSubmit={handleSubmit}
                className="relative bg-transparent border-0 rounded-2xl"
              >
                <PromptInputTextarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    !session?.user
                      ? "Type a message to get started (login required)..."
                      : input
                        ? ""
                        : placeholderText + (showCursor ? "|" : " ")
                  }
                  rows={2}
                  disabled={isSubmitting}
                  className="min-h-[100px] resize-none bg-transparent text-white placeholder:text-white/40 border-0 focus:outline-none focus:ring-0 px-5 py-4"
                  style={{ lineHeight: "1.8" }}
                />
                <PromptInputFooter className="border-t border-white/10">
                  <PromptInputTools>
                    <PromptInputSelect
                      value={model}
                      onValueChange={(value) => setModel(value)}
                    >
                      <PromptInputSelectTrigger className="text-white/70 hover:text-white">
                        <PromptInputSelectValue />
                      </PromptInputSelectTrigger>
                      <PromptInputSelectContent>
                        {models.map((m) => (
                          <PromptInputSelectItem key={m.id} value={m.id}>
                            {m.name}
                          </PromptInputSelectItem>
                        ))}
                      </PromptInputSelectContent>
                    </PromptInputSelect>
                  </PromptInputTools>
                  <PromptInputSubmit
                    disabled={!input.trim() || isSubmitting}
                    status={isSubmitting ? "submitted" : undefined}
                    className="hover:bg-white/10"
                  />
                </PromptInputFooter>
              </PromptInput>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
